import type Phaser from "phaser";
import { explosionRing, spark } from "@yjsgames/phaser-fx";
import { DEPTH } from "./depthLayers.js";
import type { FxImagePool } from "./fxImagePool.js";
import { PALETTE_NUM } from "../themes/palette.js";

const DESTRUCTION_PARTICLE_TEXTURE = "hit_spark_white";
const SHARD_TRAVEL_SCALE = 0.66;
const SHARD_VISUAL_SCALE = 0.68;
const SMOKE_TRAVEL_SCALE = 0.62;
const SMOKE_VISUAL_SCALE = 0.72;
const FLASH_SCALE = 0.62;
const RING_RADIUS_SCALE = 0.26;
const RING_END_SCALE = 1.65;

export interface EnemyShipDestructionPlanOptions {
  readonly x: number;
  readonly y: number;
  readonly displayWidth: number;
  readonly displayHeight: number;
  readonly tint: number;
  readonly isCrit?: boolean;
  readonly rng?: () => number;
}

export interface EnemyShipShardSpawn {
  readonly texture: string;
  readonly tint: number;
  readonly x: number;
  readonly y: number;
  readonly toX: number;
  readonly toY: number;
  readonly scale: number;
  readonly endScale: number;
  readonly rotation: number;
  readonly rotationDelta: number;
  readonly alpha: number;
  readonly duration: number;
  readonly delay: number;
  readonly additive: boolean;
}

export interface EnemyShipSmokeSpawn {
  readonly x: number;
  readonly y: number;
  readonly toX: number;
  readonly toY: number;
  readonly scale: number;
  readonly endScale: number;
  readonly alpha: number;
  readonly duration: number;
  readonly delay: number;
}

export interface EnemyShipDestructionPlan {
  readonly radius: number;
  readonly flashScale: number;
  readonly ringRadius: number;
  readonly shards: readonly EnemyShipShardSpawn[];
  readonly smoke: readonly EnemyShipSmokeSpawn[];
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));
}

function pickShardTexture(index: number): string {
  void index;
  return DESTRUCTION_PARTICLE_TEXTURE;
}

export function computeEnemyShipDestructionPlan(
  opts: EnemyShipDestructionPlanOptions,
): EnemyShipDestructionPlan {
  const rng = opts.rng ?? Math.random;
  const width = Math.max(1, opts.displayWidth);
  const height = Math.max(1, opts.displayHeight);
  const radius = Math.max(width, height);
  const energy = opts.isCrit ? 1.22 : 1;
  const shardCount = Math.max(6, Math.min(10, Math.round(radius / 7)));
  const smokeCount = Math.max(2, Math.min(4, Math.round(radius / 16)));
  const tintMix = [opts.tint, PALETTE_NUM.accentGold, PALETTE_NUM.damageText] as const;

  const shards: EnemyShipShardSpawn[] = [];
  for (let i = 0; i < shardCount; i += 1) {
    const baseAngle = (i / shardCount) * Math.PI * 2 - Math.PI / 2;
    const angle = baseAngle + (rng() - 0.5) * 0.72;
    const dist = radius * (0.52 + 0.45 * rng()) * energy * SHARD_TRAVEL_SCALE;
    const heat = clamp01(0.25 + rng() * 0.75);
    const isHot = heat > 0.62;
    const startOffset = radius * (0.04 + rng() * 0.08);
    const sx = opts.x + Math.cos(angle) * startOffset;
    const sy = opts.y + Math.sin(angle) * startOffset * 0.55;
    shards.push({
      texture: pickShardTexture(i),
      tint: isHot ? tintMix[1 + Math.floor(rng() * 2)]! : opts.tint,
      x: sx,
      y: sy,
      toX: sx + Math.cos(angle) * dist,
      toY: sy + Math.sin(angle) * dist * 0.58 + radius * (0.12 + rng() * 0.16),
      scale: (0.85 + rng() * 0.8) * (opts.isCrit ? 1.08 : 1) * SHARD_VISUAL_SCALE,
      endScale: 0.18 + rng() * 0.18,
      rotation: rng() * Math.PI * 2,
      rotationDelta: (rng() * 2 - 1) * Math.PI * (opts.isCrit ? 1.35 : 1),
      alpha: isHot ? 0.95 : 0.78,
      duration: 330 + rng() * 210,
      delay: rng() * 45,
      additive: isHot,
    });
  }

  const smoke: EnemyShipSmokeSpawn[] = [];
  for (let i = 0; i < smokeCount; i += 1) {
    const angle = (i / smokeCount) * Math.PI * 2 + rng() * 0.55;
    const drift = radius * (0.12 + rng() * 0.18) * SMOKE_TRAVEL_SCALE;
    const startOffset = radius * (0.03 + rng() * 0.06);
    const sx = opts.x + Math.cos(angle) * startOffset;
    const sy = opts.y + Math.sin(angle) * startOffset * 0.45;
    smoke.push({
      x: sx,
      y: sy,
      toX: sx + Math.cos(angle) * drift,
      toY: sy - radius * (0.06 + rng() * 0.1) * SMOKE_TRAVEL_SCALE,
      scale: (0.34 + rng() * 0.24) * SMOKE_VISUAL_SCALE,
      endScale: (1.2 + rng() * 0.5) * SMOKE_VISUAL_SCALE,
      alpha: 0.16 + rng() * 0.12,
      duration: 520 + rng() * 220,
      delay: 25 + rng() * 80,
    });
  }

  return {
    radius,
    flashScale: (opts.isCrit ? 1.08 : 0.9) * Math.max(0.8, radius / 48) * FLASH_SCALE,
    ringRadius: Math.max(7, radius * RING_RADIUS_SCALE),
    shards,
    smoke,
  };
}

export function emitEnemyShipDestructionFx(
  scene: Phaser.Scene,
  pool: FxImagePool,
  opts: EnemyShipDestructionPlanOptions,
): readonly Phaser.GameObjects.GameObject[] {
  const plan = computeEnemyShipDestructionPlan(opts);
  const out: Phaser.GameObjects.GameObject[] = [];

  out.push(
    explosionRing(scene, opts.x, opts.y, plan.ringRadius, {
      color: opts.isCrit ? PALETTE_NUM.damageText : PALETTE_NUM.accentGold,
      alpha: opts.isCrit ? 0.58 : 0.44,
      endScale: (opts.isCrit ? 2.55 : 2.25) * RING_END_SCALE / 2.25,
      duration: opts.isCrit ? 390 : 340,
      depth: DEPTH.projectile + 0.6,
    }),
  );
  out.push(
    spark(scene, opts.x, opts.y, DESTRUCTION_PARTICLE_TEXTURE, {
      startScale: plan.flashScale,
      endScale: plan.flashScale * 2.2,
      alpha: opts.isCrit ? 0.92 : 0.78,
      duration: 260,
      tint: opts.isCrit ? PALETTE_NUM.damageText : PALETTE_NUM.accentGold,
      depth: DEPTH.projectile + 0.7,
    }),
  );

  for (const s of plan.shards) {
    const shard = pool
      .acquire()
      .setTexture(s.texture)
      .setPosition(s.x, s.y)
      .setDepth(DEPTH.projectile + 0.65)
      .setTint(s.tint)
      .setBlendMode(s.additive ? "ADD" : "NORMAL")
      .setAlpha(s.alpha)
      .setScale(s.scale)
      .setRotation(s.rotation)
      .setActive(true)
      .setVisible(true);
    scene.tweens.add({
      targets: shard,
      x: s.toX,
      y: s.toY,
      alpha: 0,
      scale: s.scale * s.endScale,
      rotation: s.rotation + s.rotationDelta,
      delay: s.delay,
      duration: s.duration,
      ease: "Cubic.easeOut",
      onComplete: () => pool.release(shard),
    });
    out.push(shard);
  }

  for (const s of plan.smoke) {
    const puff = pool
      .acquire()
      .setTexture(DESTRUCTION_PARTICLE_TEXTURE)
      .setPosition(s.x, s.y)
      .setDepth(DEPTH.projectile + 0.45)
      .setTint(PALETTE_NUM.panelDivider)
      .setBlendMode("NORMAL")
      .setAlpha(s.alpha)
      .setScale(s.scale)
      .setRotation(0)
      .setActive(true)
      .setVisible(true);
    scene.tweens.add({
      targets: puff,
      x: s.toX,
      y: s.toY,
      alpha: 0,
      scale: s.endScale,
      delay: s.delay,
      duration: s.duration,
      ease: "Sine.easeOut",
      onComplete: () => pool.release(puff),
    });
    out.push(puff);
  }

  return out;
}
